import { LegalLayout, Section } from "./LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="June 2026" testid="terms-page">
      <p>
        These Terms of Service ("Terms") govern your access to and use of the OperaMind AI receptionist and
        communications platform, including our website and dashboard (together, the "Service"). By creating
        an account or using the Service, you agree to these Terms. If you do not agree, do not use the
        Service.
      </p>

      <Section heading="1. The Service">
        <p>
          OperaMind provides an AI-powered receptionist that can answer calls, respond to enquiries, book
          appointments, take messages and integrate with tools such as calendars, booking systems and
          payment providers. Features may change or be improved over time.
        </p>
      </Section>

      <Section heading="2. Eligibility & accounts">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>You must be at least 18 years old and able to enter into a binding contract.</li>
          <li>You are responsible for the accuracy of the information you provide and for keeping your login credentials secure.</li>
          <li>You are responsible for all activity that occurs under your account, including any staff sub-accounts you create.</li>
        </ul>
      </Section>

      <Section heading="3. Acceptable use">
        <p>You agree not to use the Service to:</p>
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Break any applicable law or regulation, including telecommunications and marketing rules.</li>
          <li>Make unsolicited, fraudulent, abusive or unlawful calls or messages.</li>
          <li>Impersonate any person or misrepresent your business or affiliation.</li>
          <li>Infringe the rights of others or upload harmful or illegal content.</li>
          <li>Attempt to disrupt, reverse-engineer or gain unauthorised access to the Service.</li>
        </ul>
      </Section>

      <Section heading="4. Phone numbers & regulatory compliance">
        <p>
          Phone numbers are provisioned through our telephony provider (Twilio) and are subject to their
          terms and to regulatory requirements. You must provide accurate verification details and supporting
          documents where required. Numbers may be suspended or withdrawn if verification fails, is revoked,
          or if the number is used in breach of these Terms or applicable regulations.
        </p>
      </Section>

      <Section heading="5. Call recording responsibilities">
        <p>
          You are responsible for complying with all laws relating to call recording, monitoring and data
          protection in the jurisdictions where you operate, including informing callers that calls may be
          recorded and having a lawful basis for processing their data. OperaMind processes call data on your
          behalf as described in our{" "}
          <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>.
        </p>
      </Section>

      <Section heading="6. AI-generated responses">
        <p>
          The Service uses AI to generate responses, summaries and suggestions. While we work to keep the AI
          accurate and to only use the business information you configure, AI output may occasionally be
          incomplete or incorrect. You are responsible for reviewing your configuration (services, prices,
          hours and policies) and for the outcomes of calls handled on your behalf. OperaMind is not liable
          for decisions made based on AI-generated content.
        </p>
      </Section>

      <Section heading="7. Subscriptions, billing & trials">
        <ul className="list-disc pl-6 space-y-1.5">
          <li>Paid plans are billed in advance on a recurring basis through our payment provider (Stripe).</li>
          <li>Plans include a monthly allowance of usage (e.g. call minutes); additional usage may incur charges as described at sign-up.</li>
          <li>Trials, where offered, convert to a paid subscription unless cancelled before the trial ends.</li>
          <li>Fees are non-refundable except where required by law. You can cancel at any time; access continues until the end of the current billing period.</li>
        </ul>
      </Section>

      <Section heading="8. Third-party integrations">
        <p>
          The Service integrates with third-party tools you choose to connect (e.g. Google Calendar, Outlook,
          booking systems and payment providers). Your use of those tools is governed by their own terms, and
          OperaMind is not responsible for third-party services or their availability.
        </p>
      </Section>

      <Section heading="9. Intellectual property">
        <p>
          OperaMind and its licensors own all rights in the Service, including software, branding and
          content. We grant you a limited, non-exclusive, non-transferable licence to use the Service in
          accordance with these Terms. You retain ownership of your own business data and content.
        </p>
      </Section>

      <Section heading="10. Service availability">
        <p>
          We aim to keep the Service available and reliable but do not guarantee uninterrupted or error-free
          operation. We may perform maintenance, updates or changes, and may suspend the Service where
          necessary for security, legal or operational reasons.
        </p>
      </Section>

      <Section heading="11. Suspension & termination">
        <p>
          We may suspend or terminate your account if you breach these Terms, fail to pay fees, or use the
          Service unlawfully. You may close your account at any time from the dashboard or by contacting us.
          On termination, your right to use the Service ends and we may delete your data in line with our
          retention practices.
        </p>
      </Section>

      <Section heading="12. Limitation of liability">
        <p>
          To the maximum extent permitted by law, OperaMind is not liable for indirect, incidental or
          consequential losses, or for loss of profits, revenue, data or goodwill. Our total liability arising
          out of or relating to the Service is limited to the amount you paid to us in the 12 months before
          the event giving rise to the claim. Nothing in these Terms limits liability that cannot be limited
          by law.
        </p>
      </Section>

      <Section heading="13. Indemnity">
        <p>
          You agree to indemnify OperaMind against claims, losses and expenses arising from your use of the
          Service in breach of these Terms or applicable law, including how you handle calls, recordings and
          the personal data of your own customers.
        </p>
      </Section>

      <Section heading="14. Governing law">
        <p>
          These Terms are governed by the laws of England and Wales, and the courts of England and Wales
          have exclusive jurisdiction over any disputes.
        </p>
      </Section>

      <Section heading="15. Changes to these Terms">
        <p>
          We may update these Terms from time to time. Material changes will be notified through the Service
          or by email, and your continued use after changes take effect constitutes acceptance.
        </p>
      </Section>

      <Section heading="16. Contact us">
        <p>
          Questions about these Terms? Email us at{" "}
          <a href="mailto:admin@operamind.uk" className="text-blue-600 hover:underline">admin@operamind.uk</a>.
        </p>
      </Section>
    </LegalLayout>
  );
}
